import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import { Subject, fromEvent, pipe } from 'rxjs';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { CONSTANTS } from 'src/app/core/constants/constants';
import { RangeSelection } from './range-selection.interface';

@Directive({
  selector: '[appRangeSelection]',
})
export class RangeSelectionDirective implements OnDestroy, OnChanges {
  @Input() appRangeSelection = false;
  @Input() selectionClass = 'selected';
  @Input() cellClass = '.anflex-table-body-cell';

  @Output() selectEvent = new EventEmitter<RangeSelection>();

  selectedCells = new Set<HTMLTableCellElement>();

  private readonly table: HTMLTableElement;
  private startCell: HTMLTableCellElement = null;
  private minorCell: HTMLTableCellElement = null;
  private cellIndexMap = new Map<HTMLTableCellElement, { row: number; column: number }>();
  private selecting: boolean;
  private destroy$ = new Subject<void>();

  constructor(private zone: NgZone, { nativeElement }: ElementRef<HTMLTableElement>) {
    this.table = nativeElement;
  }

  ngOnChanges(): void {
    if (!this.appRangeSelection) {
      this.destroy$.next();
    } else {
      this.zone.runOutsideAngular(() => this.initListeners());
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  private initListeners() {
    const withCell = pipe(
      map((event: MouseEvent) => ({
        event,
        cell: (event.target as HTMLTableCellElement).closest<HTMLTableCellElement>(this.cellClass),
      })),
      filter(({ cell }) => !!cell),
    );
    const mouseDown$ = fromEvent<MouseEvent>(this.table, CONSTANTS.MOUSE_EVENT.DOWN).pipe(
      filter((event) => {
        return (
          event.button === CONSTANTS.MOUSE_EVENT.LEFT ||
          event.button === CONSTANTS.MOUSE_EVENT.RIGHT
        );
      }),
      withCell,
      tap(this.startSelection),
    );
    const mouseOver$ = fromEvent<MouseEvent>(this.table, CONSTANTS.MOUSE_EVENT.OVER);
    const mouseUp$ = fromEvent<MouseEvent>(document, CONSTANTS.MOUSE_EVENT.UP).pipe(
      tap(() => (this.selecting = false)),
    );
    this.handleOutsideClick();

    mouseDown$
      .pipe(
        switchMap(() => mouseOver$.pipe(takeUntil(mouseUp$))),
        takeUntil(this.destroy$),
        withCell,
      )
      .subscribe(({ cell }) => {
        this.select({ cell });
      });
  }

  private handleOutsideClick(): void {
    fromEvent(document, CONSTANTS.MOUSE_EVENT.UP)
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        if (!this.selecting && !this.table.contains(event.target as HTMLTableCellElement)) {
          this.clearCells();
        }
      });
  }

  private startSelection = ({ cell, event }: { event: MouseEvent; cell: HTMLTableCellElement }) => {
    this.updateCellIndexes();
    if (!event.ctrlKey && !event.shiftKey) {
      this.clearCells();
    }

    if (event.shiftKey) {
      this.select({ cell });
    }

    this.selecting = true;
    this.startCell = cell;
    this.minorCell = this.getMinorCell(cell, this.startCell);

    if (!event.shiftKey) {
      if (this.selectedCells.has(cell)) {
        this.selectedCells.delete(cell);
      } else {
        this.selectedCells.add(cell);
      }
      cell.classList.toggle(this.selectionClass);
      this.emitSelectionEvent();
    }
  };

  getMinorCell(cell1, cell2): HTMLTableCellElement {
    const cell1Row = cell1.closest(CONSTANTS.ROW_TAG).rowIndex;
    const cell1Col = cell1.cellIndex;
    const cell2Row = cell2.closest(CONSTANTS.ROW_TAG).rowIndex;
    const cell2Col = cell2.cellIndex;
    if (cell1Row < cell2Row || cell1Col < cell2Col) {
      return cell1;
    }

    return cell2;
  }

  private select = ({ cell }: { cell: HTMLTableCellElement }) => {
    this.clearCells();
    this.getCellsBetween(this.startCell, cell).forEach((item) => {
      this.selectedCells.add(item);
      item.classList.add(this.selectionClass);
    });
    this.minorCell = this.getMinorCell(cell, this.startCell);
    this.emitSelectionEvent();
  };

  emitSelectionEvent(): void {
    this.selectEvent.emit({
      startCell: {
        row: this.startCell.closest(CONSTANTS.ROW_TAG).rowIndex,
        col: this.startCell.cellIndex,
      },
      minorCell: {
        row: this.minorCell.closest(CONSTANTS.ROW_TAG).rowIndex,
        col: this.minorCell.cellIndex,
      },
      cells: this.selectedCells,
    });
  }

  public clearCells(): void {
    Array.from(this.selectedCells).forEach((cell) => {
      cell.classList.remove(this.selectionClass);
    });
    this.selectedCells.clear();
    this.selectEvent.emit(null);
  }

  private getCellsBetween(
    start: HTMLTableCellElement,
    end: HTMLTableCellElement,
  ): HTMLTableCellElement[] {
    if (!start || !end || start === end) {
      return [];
    }

    const startCoords = this.cellIndexMap.get(start);
    const endCoords = this.cellIndexMap.get(end);

    if (!startCoords || !endCoords) {
      return [];
    }

    const boundaries = {
      top: Math.min(startCoords.row, endCoords.row),
      right: Math.max(startCoords.column, endCoords.column),
      bottom: Math.max(startCoords.row, endCoords.row),
      left: Math.min(startCoords.column, endCoords.column),
    };

    const cells = [];

    this.iterateCells(this.table, (cell) => {
      const { column, row } = this.cellIndexMap.get(cell);
      if (
        column >= boundaries.left &&
        column <= boundaries.right &&
        row >= boundaries.top &&
        row <= boundaries.bottom
      ) {
        cells.push(cell);
      }
    });

    return cells;
  }

  private updateCellIndexes(): void {
    this.cellIndexMap.clear();
    const matrix = [];
    this.iterateCells(this.table, (cell, y, x) => {
      for (let spanX = x; spanX < x; spanX++) {
        for (let spanY = y; spanY < y; spanY++) {
          (matrix[spanY] = matrix[spanY] || [])[spanX] = 1;
        }
      }
      this.cellIndexMap.set(cell, { row: y, column: x });
    });
  }

  iterateCells(
    table: HTMLTableElement,
    callback: (cell: HTMLTableCellElement, y: number, x: number) => void,
  ): void {
    for (let y = 0; y < table.rows.length; y++) {
      for (let x = 0; x < table.rows[y].cells.length; x++) {
        callback(table.rows[y].cells[x], y, x);
      }
    }
  }
}
